import type { ReactNode } from 'react'
import type { Sku, SkuPriceOptionsResponse, SkuPricesResponse, SkuPriceOption } from '@/types/interfaces/sku'
import { isInPersonModality } from '@/lib/sku/inPersonVisitRollout'
import { VisitOptionVideoVisitIcon } from '@/components/Icons/VisitOptionVideoVisitIcon'
import Chat from '@/components/Icons/SelfServe/Chat'

export type VisitType = 'video' | 'chat' | 'econsult' | 'in-person-visit'
export type CompactVisitType = Exclude<VisitType, 'in-person-visit'>

/**
 * Derives the set of visit types that have actionable data in the
 * SKU price-options response. A type is considered "available" when:
 *   - it is a known type (`video`/`chat`/`econsult`/`in-person-visit`,
 *     or the legacy API `referral` which is normalized to
 *     `in-person-visit`), AND
 *   - it has price data (`cash` or `insurance`) — except for the
 *     in-person modality which is eligible regardless of price (pricing
 *     fallbacks like `typical_copay` are handled by the office visit
 *     option card).
 */
export function getAvailableVisitTypes(prices: SkuPriceOptionsResponse | null): VisitType[] {
    if (!prices || !prices.sku_price_mapping) return []
    const types = new Set<VisitType>()
    prices.sku_price_mapping.forEach((mapping) => {
        let pricesArr: SkuPricesResponse[] = []
        if (Array.isArray(mapping.prices)) {
            pricesArr = mapping.prices as SkuPricesResponse[]
        } else if (mapping.prices && typeof mapping.prices === 'object') {
            pricesArr = [mapping.prices as SkuPricesResponse]
        }
        pricesArr.forEach((price) => {
            price.options.forEach((option: SkuPriceOption) => {
                const hasPrice = option.cash != null || option.insurance != null
                const isInPerson = isInPersonModality(option.type)
                const isKnownType =
                    option.type === 'video' || option.type === 'chat' || option.type === 'econsult' || isInPerson
                const isEligible = isInPerson || hasPrice
                if (isKnownType && isEligible) {
                    // Call the type-predicate inline so TS narrows `option.type`
                    // to `'video' | 'chat' | 'econsult'` in the else branch
                    // (aliased-condition narrowing on a `const` doesn't reach
                    // through a ternary in TS for user-defined guards here).
                    types.add(isInPersonModality(option.type) ? 'in-person-visit' : option.type)
                }
            })
        })
    })
    return Array.from(types)
}

function findCompactPriceOption(
    prices: SkuPriceOptionsResponse | null,
    skuId: string,
    type: CompactVisitType,
): SkuPriceOption | undefined {
    const mapping = prices?.sku_price_mapping?.find((m) => String(m.id) === String(skuId))
    return [mapping?.prices]
        .flat()
        .flatMap((p) => p?.options ?? [])
        .find((o) => o?.type === type)
}

export interface CompactVisitPricing {
    price: string
    insuranceSubtext: string
    isUpsellInsurance?: boolean // True if the user is signed in without insurance.
}

/**
 * Returns formatted price and qualifier subtext for compact "Other ways to start" rows.
 *
 * Chat: cash price with "self-pay only" when cash exists; no subtext if cash is missing.
 *
 * Video and econsult subtext (no subtext when there is no price):
 *   1. Guest + insurance estimate → "typical copay"
 *   2. Signed in + no insurance on file → upsell; "or $X self-pay" when cash exists
 *   3. Signed in + active insurance + insurance ≤ cash → "est. insurance"
 *   4. Signed in + active insurance + cash displayed → "self-pay only"
 *   5. Other signed-in cases → "typical copay"
 *
 * Display price: lowest of cash and insurance, except signed-in without insurance
 * uses cash when available (insurance estimate is not shown as the headline).
 */
export function getCompactVisitPricing(
    prices: SkuPriceOptionsResponse | null,
    sku: Sku,
    type: CompactVisitType,
): CompactVisitPricing {
    const fmt = (cents: number) => `$${Math.round(cents / 100)}`
    const opt = findCompactPriceOption(prices, sku.id, type)

    if (type === 'chat') {
        const cash = opt?.cash
        return {
            price: cash != null ? fmt(cash) : '',
            insuranceSubtext: cash != null ? 'self-pay only' : '',
            isUpsellInsurance: false,
        }
    }

    const insuranceCents = opt?.insurance ?? null
    const cashCents = opt?.cash ?? null
    const insurancePrice = insuranceCents != null ? fmt(insuranceCents) : undefined
    const cashPrice = cashCents != null ? fmt(cashCents) : undefined
    const loggedInWithoutInsurance = sku.authenticated && !sku.patient_has_active_insurance
    const insuranceIsLower =
        insuranceCents != null && (cashCents == null || insuranceCents <= cashCents)

    let preferredPrice = ''
    if (loggedInWithoutInsurance && cashPrice) {
        preferredPrice = cashPrice
    } else if (insurancePrice && cashPrice) {
        preferredPrice = insuranceIsLower ? insurancePrice : cashPrice
    } else if (insurancePrice) {
        preferredPrice = insurancePrice
    } else if (cashPrice) {
        preferredPrice = cashPrice
    }

    let insuranceSubtext = ''
    let isUpsellInsurance = false
    if (preferredPrice !== '') {
        if (loggedInWithoutInsurance) {
            isUpsellInsurance = true
            if (cashPrice) {
                insuranceSubtext = `or ${cashPrice} self-pay`
            }
        } else if (!sku.authenticated) {
            insuranceSubtext = insurancePrice ? 'typical copay' : 'self-pay only'
        } else if (sku.patient_has_active_insurance && insuranceIsLower) {
            insuranceSubtext = 'est. insurance'
        } else if (sku.patient_has_active_insurance && cashPrice && preferredPrice === cashPrice) {
            insuranceSubtext = 'self-pay only'
        } else {
            insuranceSubtext = 'typical copay'
        }
    }

    return {
        price: preferredPrice,
        insuranceSubtext,
        isUpsellInsurance,
    }
}

/** Title + icon used by the compact "Other ways to start" rows. */
export function getCompactMeta(type: CompactVisitType): { title: string; icon: ReactNode } {
    switch (type) {
        case 'video':
            return { title: 'Video visit', icon: <VisitOptionVideoVisitIcon size={20} /> }
        case 'econsult':
            return { title: 'Specialist review', icon: <VisitOptionVideoVisitIcon size={20} /> }
        case 'chat':
            return { title: 'Chat visit', icon: <Chat size={18} /> }
    }
}
